'use client';

/**
 * V2Footer - prostaysites.com footer.
 *
 * Same dark editorial composition as the prostay.com footer (oversized
 * wordmark, tight smallcaps columns, language chips) with an IA sized for
 * this site: one column of local pages, one column of contextual
 * prostay.com platform links (the one-way authority flow from the SEO
 * strategy), and a "Part of the Prostay platform" brand line.
 */

import { useTranslations } from 'next-intl';
import { Link } from '@/i18n/navigation';
/* Plain next/link for the language-switcher chips only — see
 * useLocaleSwitcher comments. */
import NextLink from 'next/link';
import { useLocaleSwitcher } from '@/components/v2/useLocaleSwitcher';
import { openPreferences } from '@/components/v2/cookieConsent/storage';
import V2Logo from './V2Logo';

const PROSTAY = 'https://www.prostay.com';

/* Local pages (locale-aware links). */
const SITE_LINKS = [
  { href: '/templates/',    key: 'templates' },
  { href: '/features/',     key: 'features' },
  { href: '/pricing/',      key: 'pricing' },
  { href: '/request-demo/', key: 'requestDemo' }
];

/* Comparison pages (locale-aware links). */
const COMPARE_LINKS = [
  { href: '/compare/wix/',         key: 'vsWix' },
  { href: '/compare/squarespace/', key: 'vsSquarespace' },
  { href: '/compare/wordpress/',   key: 'vsWordpress' }
];

/* Contextual cross-links to the mothership. Plain external anchors —
 * they leave this site. */
const PLATFORM_LINKS = [
  { href: `${PROSTAY}/booking-engine/`,             key: 'bookingEngine' },
  { href: `${PROSTAY}/property-management-system/`, key: 'pms' },
  { href: `${PROSTAY}/channel-manager/`,            key: 'channelManager' },
  { href: `${PROSTAY}/prostay-pay/`,                key: 'prostayPay' },
  { href: `${PROSTAY}/blog/`,                       key: 'blog' }
];

const LEGAL_LINKS = [
  { href: `${PROSTAY}/terms-and-conditions/`, key: 'termsOfService' },
  { href: `${PROSTAY}/privacy-policy/`,       key: 'privacyPolicy' },
  { href: `${PROSTAY}/cookies-policy/`,       key: 'cookies' }
];

export default function V2Footer() {
  const t = useTranslations('Layout.Footer');
  const year = new Date().getFullYear();
  const localeOptions = useLocaleSwitcher();

  return (
    <footer className="relative overflow-hidden bg-gray-950 text-gray-300">
      <div
        aria-hidden
        className="bg-grid-dark pointer-events-none absolute inset-0 opacity-60"
      />

      <div className="container-page relative pt-20 pb-16 sm:pt-24 sm:pb-20">
        {/* Brand row */}
        <div className="flex flex-col gap-6 border-b border-white/10 pb-10 sm:flex-row sm:items-end sm:justify-between sm:pb-14">
          <Link
            href="/"
            aria-label={t('brand.homeAria')}
            className="inline-flex items-center text-white transition-opacity hover:opacity-80"
          >
            <V2Logo className="h-7 sm:h-8" />
          </Link>
          <p className="max-w-md text-[11px] font-bold uppercase tracking-[0.18em] text-gray-500 sm:text-right">
            {t('brand.tagline')}
          </p>
        </div>

        {/* Link grid */}
        <div className="mt-14 grid gap-12 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,2fr)] lg:gap-20">
          <div className="flex flex-col gap-6">
            <p className="max-w-sm text-sm leading-relaxed text-gray-400">
              {t('brand.description')}
            </p>
            <p className="text-sm text-gray-500">
              {t('brand.partOf')}{' '}
              <a
                href={PROSTAY}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-300 underline decoration-gray-600 underline-offset-4 transition-colors hover:text-white"
              >
                prostay.com
              </a>
            </p>
          </div>

          <div className="grid grid-cols-2 gap-8 sm:grid-cols-3 sm:gap-6">
            <FooterColumn
              id="footer-col-site"
              title={t('columns.instantSite')}
            >
              {SITE_LINKS.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="inline-flex items-center text-sm tracking-tight text-gray-300 transition-colors hover:text-white"
                  >
                    {t(`links.${link.key}`)}
                  </Link>
                </li>
              ))}
            </FooterColumn>

            <FooterColumn
              id="footer-col-compare"
              title={t('columns.compare')}
            >
              {COMPARE_LINKS.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="inline-flex items-center text-sm tracking-tight text-gray-300 transition-colors hover:text-white"
                  >
                    {t(`links.${link.key}`)}
                  </Link>
                </li>
              ))}
            </FooterColumn>

            <FooterColumn
              id="footer-col-platform"
              title={t('columns.platform')}
            >
              {PLATFORM_LINKS.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    className="inline-flex items-center text-sm tracking-tight text-gray-300 transition-colors hover:text-white"
                  >
                    {t(`links.${link.key}`)}
                  </a>
                </li>
              ))}
            </FooterColumn>
          </div>
        </div>

        <div className="mt-20 h-px w-full bg-white/10" />

        {/* Language switcher */}
        <nav aria-label={t('language.languagesAria')} className="mt-10">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gray-500">
            {t('language.label')}
          </p>
          <ul className="mt-4 flex flex-wrap gap-2">
            {localeOptions.map((opt) => {
              const baseClasses =
                'inline-flex h-9 items-center gap-2 rounded-full border px-3 text-[11px] font-bold uppercase tracking-[0.16em] transition-colors';
              const inner = (
                <>
                  <span className="font-bold">{opt.code.toUpperCase()}</span>
                  <span className="hidden text-[11px] font-medium normal-case tracking-normal opacity-80 sm:inline">
                    {opt.label}
                  </span>
                </>
              );
              if (opt.active) {
                return (
                  <li key={opt.code}>
                    <span
                      aria-current="page"
                      title={opt.label}
                      className={`${baseClasses} border-white bg-white text-gray-950`}
                    >
                      {inner}
                    </span>
                  </li>
                );
              }
              return (
                <li key={opt.code}>
                  <NextLink
                    href={opt.href}
                    hrefLang={opt.code}
                    title={opt.label}
                    onClick={opt.onSelect}
                    className={`${baseClasses} border-white/15 text-gray-300 hover:border-white hover:text-white`}
                  >
                    {inner}
                  </NextLink>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="mt-12 flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <p className="text-xs text-gray-500">
            {t('legal.copyright', {year})}
          </p>
          <ul className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-gray-500">
            {LEGAL_LINKS.map((link) => (
              <li key={link.key}>
                <a href={link.href} className="transition-colors hover:text-white">
                  {t(`legal.links.${link.key}`)}
                </a>
              </li>
            ))}
            <li>
              <button
                type="button"
                onClick={openPreferences}
                className="cursor-pointer bg-transparent p-0 text-xs text-gray-500 transition-colors hover:text-white"
              >
                {t('legal.cookieSettings')}
              </button>
            </li>
          </ul>
        </div>
      </div>
    </footer>
  );
}

function FooterColumn({ id, title, children }) {
  /* Column labels are navigation labels, not document headings — emit as
   * <p> with aria-labelledby on the list (mirrors prostay.com a11y fix). */
  return (
    <div>
      <p
        id={id}
        className="text-[11px] font-bold uppercase tracking-[0.18em] text-gray-500"
      >
        {title}
      </p>
      <ul aria-labelledby={id} className="mt-5 space-y-3">
        {children}
      </ul>
    </div>
  );
}
